import { useCallback, useMemo, useState } from 'react';

import { useDismissibleSelection } from './useDismissibleSelection';

export const WORKSPACE_RESOURCE_LIST_COLUMN_OPTIONS = [
    { key: 'type', label: 'Type', sortable: true },
    { key: 'collaborators', label: 'Collaborators', sortable: false },
    { key: 'createdAt', label: 'Created At', sortable: true },
    { key: 'updatedAt', label: 'Last Updated', sortable: true },
] as const;

export type WorkspaceResourceListColumnKey = (typeof WORKSPACE_RESOURCE_LIST_COLUMN_OPTIONS)[number]['key'];
export type WorkspaceResourceListSortKey = WorkspaceResourceListColumnKey | 'name';
export type WorkspaceResourceListSortDirection = 'ascending' | 'descending';
export type WorkspaceResourceListExportFormat = 'csv' | 'json';

export interface WorkspaceResourceListSortState {
    direction: WorkspaceResourceListSortDirection;
    key: WorkspaceResourceListSortKey;
}

export interface WorkspaceResourceListControls {
    allSelected: boolean;
    columns: WorkspaceResourceListColumnKey[];
    exportCurrentView: (format: WorkspaceResourceListExportFormat, label: string) => void;
    indeterminate: boolean;
    isSelected: (id: string) => boolean;
    itemCount: number;
    onGlobalSortChange: (key?: string) => void;
    resetColumns: () => void;
    selectedCount: number;
    setColumnAt: (index: number, key: WorkspaceResourceListColumnKey) => void;
    sortState?: WorkspaceResourceListSortState;
    toggleAll: (checked: boolean) => void;
    toggleColumnSort: (key: WorkspaceResourceListSortKey) => void;
    toggleSelected: (id: string, checked: boolean) => void;
}

interface UseWorkspaceResourceListControlsOptions<T> {
    getId: (item: T) => string;
    getSortValue: (item: T, key: WorkspaceResourceListSortKey) => Date | number | string | null | undefined;
    items: T[];
}

const compareValues = (left: Date | number | string | null | undefined, right: typeof left) => {
    if (left === right) {
        return 0;
    }

    if (left === null || left === undefined || left === '') {
        return 1;
    }

    if (right === null || right === undefined || right === '') {
        return -1;
    }

    const leftValue = left instanceof Date ? left.getTime() : left;
    const rightValue = right instanceof Date ? right.getTime() : right;
    const datePattern = /^\d{4}-\d{2}-\d{2}(?:[T\s]|$)/u;
    const leftDate = typeof leftValue === 'string' && datePattern.test(leftValue) ? Date.parse(leftValue) : Number.NaN;
    const rightDate =
        typeof rightValue === 'string' && datePattern.test(rightValue) ? Date.parse(rightValue) : Number.NaN;

    if (Number.isFinite(leftDate) && Number.isFinite(rightDate)) {
        return leftDate - rightDate;
    }

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return leftValue - rightValue;
    }

    return String(leftValue).localeCompare(String(rightValue), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
};

const isEmptySortValue = (value: Date | number | string | null | undefined) =>
    value === null || value === undefined || value === '';

const DEFAULT_COLUMNS: WorkspaceResourceListColumnKey[] = ['collaborators', 'updatedAt', 'createdAt'];

const normalizeExportValue = (value: Date | number | string | null | undefined) => {
    if (value instanceof Date) {
        return value.toISOString();
    }

    return value ?? '';
};

const escapeCsvValue = (value: ReturnType<typeof normalizeExportValue>) => `"${String(value).replace(/"/gu, '""')}"`;

const getExportFileName = (label: string, format: WorkspaceResourceListExportFormat) =>
    `${
        label
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/gu, '-') || 'workspace-resources'
    }-${new Date().toISOString().slice(0, 10)}.${format}`;

export const useWorkspaceResourceListControls = <T>({
    getId,
    getSortValue,
    items,
}: UseWorkspaceResourceListControlsOptions<T>) => {
    const [columns, setColumns] = useState<WorkspaceResourceListColumnKey[]>(DEFAULT_COLUMNS);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
    const [sortState, setSortState] = useState<WorkspaceResourceListSortState | undefined>({
        direction: 'descending',
        key: 'updatedAt',
    });
    const itemIds = useMemo(() => items.map(getId), [getId, items]);
    const itemIdSet = useMemo(() => new Set(itemIds), [itemIds]);

    const visibleSelectedIds = useMemo(
        () => new Set([...selectedIds].filter((id) => itemIdSet.has(id))),
        [itemIdSet, selectedIds],
    );
    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    useDismissibleSelection({ active: visibleSelectedIds.size > 0, onDismiss: clearSelection });

    const sortedItems = useMemo(() => {
        if (!sortState) {
            return items;
        }

        const direction = sortState.direction === 'ascending' ? 1 : -1;

        return items
            .map((item, index) => ({ index, item }))
            .sort((left, right) => {
                const leftValue = getSortValue(left.item, sortState.key);
                const rightValue = getSortValue(right.item, sortState.key);

                if (isEmptySortValue(leftValue) && isEmptySortValue(rightValue)) {
                    return left.index - right.index;
                }

                if (isEmptySortValue(leftValue) && !isEmptySortValue(rightValue)) {
                    return 1;
                }

                if (!isEmptySortValue(leftValue) && isEmptySortValue(rightValue)) {
                    return -1;
                }

                const comparison = compareValues(leftValue, rightValue);

                return comparison === 0 ? left.index - right.index : comparison * direction;
            })
            .map(({ item }) => item);
    }, [getSortValue, items, sortState]);

    const toggleSelected = useCallback((id: string, checked: boolean) => {
        setSelectedIds((current) => {
            const next = new Set(current);

            if (checked) {
                next.add(id);
            } else {
                next.delete(id);
            }

            return next;
        });
    }, []);

    const toggleAll = useCallback(
        (checked: boolean) => {
            if (!checked) {
                clearSelection();
                return;
            }

            setSelectedIds(new Set(itemIds));
        },
        [clearSelection, itemIds],
    );

    const toggleColumnSort = useCallback((key: WorkspaceResourceListSortKey) => {
        setSortState((current) => {
            if (current?.key !== key) {
                return { direction: 'ascending', key };
            }

            if (current.direction === 'ascending') {
                return { direction: 'descending', key };
            }

            return undefined;
        });
    }, []);

    const onGlobalSortChange = useCallback((key?: string) => {
        if (!key || key === 'clearSort') {
            setSortState(undefined);
            return;
        }

        const normalizedKey: WorkspaceResourceListSortKey =
            key === 'alphabetical' ? 'name' : (key as WorkspaceResourceListSortKey);

        setSortState({
            direction: normalizedKey === 'name' ? 'ascending' : 'descending',
            key: normalizedKey,
        });
    }, []);

    const setColumnAt = useCallback((index: number, key: WorkspaceResourceListColumnKey) => {
        setColumns((current) => {
            const next = [...current];
            const existingIndex = next.indexOf(key);

            if (existingIndex >= 0) {
                [next[index], next[existingIndex]] = [next[existingIndex], next[index]];
            } else {
                next[index] = key;
            }

            return next;
        });
    }, []);

    const resetColumns = useCallback(() => setColumns(DEFAULT_COLUMNS), []);

    const exportCurrentView = useCallback(
        (format: WorkspaceResourceListExportFormat, label: string) => {
            const exportKeys = ['name', 'type', 'createdAt', 'updatedAt'] as const;
            const exportRows = sortedItems.map((item) =>
                Object.fromEntries(exportKeys.map((key) => [key, normalizeExportValue(getSortValue(item, key))])),
            );
            const content =
                format === 'json'
                    ? JSON.stringify(exportRows, null, 2)
                    : [
                          exportKeys.map(escapeCsvValue).join(','),
                          ...exportRows.map((row) => exportKeys.map((key) => escapeCsvValue(row[key])).join(',')),
                      ].join('\n');
            const objectUrl = window.URL.createObjectURL(
                new Blob([content], { type: format === 'json' ? 'application/json' : 'text/csv;charset=utf-8' }),
            );
            const link = document.createElement('a');

            link.href = objectUrl;
            link.download = getExportFileName(label, format);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        },
        [getSortValue, sortedItems],
    );

    const allSelected = itemIds.length > 0 && itemIds.every((id) => visibleSelectedIds.has(id));
    const indeterminate = visibleSelectedIds.size > 0 && !allSelected;
    const controls = useMemo<WorkspaceResourceListControls>(
        () => ({
            allSelected,
            columns,
            exportCurrentView,
            indeterminate,
            isSelected: (id) => visibleSelectedIds.has(id),
            itemCount: itemIds.length,
            onGlobalSortChange,
            resetColumns,
            selectedCount: visibleSelectedIds.size,
            setColumnAt,
            sortState,
            toggleAll,
            toggleColumnSort,
            toggleSelected,
        }),
        [
            allSelected,
            columns,
            exportCurrentView,
            indeterminate,
            itemIds.length,
            onGlobalSortChange,
            resetColumns,
            setColumnAt,
            sortState,
            toggleAll,
            toggleColumnSort,
            toggleSelected,
            visibleSelectedIds,
        ],
    );

    return { controls, sortedItems };
};
